import { invariant } from '@seen/core/invariant'
import { parseReferenceString } from '@seen/core/utils'
import type { Node, ReactFlowInstance } from '@xyflow/react'
import { useCallback, useContext } from 'react'
import type { To } from 'react-router'
import { DeskContext } from '#src/contexts/DeskContext.tsx'
import { NodeContext } from '#src/contexts/NodeContext.ts'
import { ReactFlowContext } from '#src/contexts/ReactFlowContext.ts'

export function useAddFlowNodeFromUrl(url: To): (() => void) | null {
  const reactFlow = useContext(ReactFlowContext)
  const nodeId = useContext(NodeContext)
  const { setNodes } = useContext(DeskContext)

  const addFlowNodeFromUrl = useCallback(() => {
    const urlString = typeof url === 'string' ? url : url.pathname
    invariant(urlString, 'urlString is required')
    invariant(reactFlow, 'reactFlow is required')
    invariant(nodeId, 'nodeId is required')
    const newNode = nodeFromUrl(urlString, reactFlow, nodeId)
    invariant(newNode, 'newNode is required')
    invariant(setNodes, 'setNodes is required')
    setNodes((nds) => nds.concat(newNode))
  }, [reactFlow, nodeId, setNodes, url])

  if (!reactFlow || !nodeId || !setNodes || !canAddFlowNodeFromUrl(url)) {
    return null
  }

  return addFlowNodeFromUrl
}

function canAddFlowNodeFromUrl(url: To): boolean {
  const urlString = typeof url === 'string' ? url : url.pathname
  return !!urlString?.match(/\/lead\/[^/]+\/?$/)
}

function nodeFromUrl(
  url: string,
  reactFlow: ReactFlowInstance,
  nodeId: string
): Node | null {
  if (url.match(/\/lead\/[^/]+\/?$/)) {
    const leadId = url.split('/lead/')[1].split('/')[0]
    const node = reactFlow.getNode(nodeId)
    if (!node) {
      return null
    }
    const {
      position: { x, y },
      measured,
      width,
    } = node
    return {
      id: `lead-${leadId}-${Date.now()}`,
      type: 'lead',
      data: {
        lead: parseReferenceString(`Patient/${leadId}`),
      },
      position: {
        x: x + (measured?.width ?? width ?? 0) + 40,
        y,
      },
      dragHandle: '.drag-handle',
    }
  }
  return null
}
